import { useEffect, useState } from "react";
import api, { photoUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Camera, AlertTriangle, Boxes, ImageOff } from "lucide-react";

const TYPES = [
  { key: "after_service", label: "After service", icon: Camera },
  { key: "damage", label: "Damages", icon: AlertTriangle },
  { key: "inventory", label: "Inventory", icon: Boxes },
];

function Gallery({ type }) {
  const [photos, setPhotos] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get(`/photos?type=${type}`).then((r) => setPhotos(r.data));
  }, [type]);

  if (photos.length === 0)
    return (
      <Card className="p-12 text-center">
        <ImageOff className="h-9 w-9 text-muted-foreground mx-auto mb-3" strokeWidth={1.3} />
        <p className="text-muted-foreground text-sm">No photos in this category yet.</p>
      </Card>
    );

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((p) => (
          <Card
            key={p.id}
            className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setPreview(p)}
            data-testid={`review-photo-${p.id}`}
          >
            <div
              className="aspect-square bg-cover bg-center bg-muted"
              style={{ backgroundImage: `url('${photoUrl(p.storage_path)}')` }}
            />
            <div className="p-2.5">
              <p className="text-xs font-medium truncate">{p.property_name}</p>
              {p.checklist_item && (
                <Badge variant="secondary" className="text-[10px] mt-1">
                  {p.checklist_item}
                </Badge>
              )}
              {p.note && (
                <p className="text-[11px] text-muted-foreground mt-1 truncate">{p.note}</p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">
                {p.uploaded_by_name} · {new Date(p.created_at).toLocaleDateString()}
              </p>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-2xl p-2">
          {preview && (
            <img
              src={photoUrl(preview.storage_path)}
              alt={preview.checklist_item || preview.type}
              className="w-full rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PhotosReview() {
  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">Review</p>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Photos &amp; Damages</h1>
      </div>

      <Tabs defaultValue="after_service">
        <TabsList className="mb-6">
          {TYPES.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
              <t.icon className="h-4 w-4 mr-2" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TYPES.map((t) => (
          <TabsContent key={t.key} value={t.key}>
            <Gallery type={t.key} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
